/* Shared pure measurement/read contract. No storage, network, identity or consent effects. */
(function (root, factory) {
  'use strict';
  const contract = factory();
  if (typeof module === 'object' && module.exports) module.exports = contract;
  else root.FwdMeasurementContract = contract;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';
  const groups = {
    'filter.p.m.custom.brand': ['brand', '브랜드'],
    'filter.p.vendor': ['brand', '브랜드'],
    'filter.p.m.custom.size': ['tagged_size', '표기 사이즈'],
    'filter.p.m.custom.size_recommend': ['recommended_size', '추천 사이즈'],
    'filter.p.m.custom.ai_size_recommend': ['recommended_size', '추천 사이즈'],
    'filter.p.m.custom.fwd_recommended_size': ['recommended_size', '추천 사이즈'],
    'filter.v.option.size': ['variant_size', '옵션 사이즈'],
    'filter.v.option.사이즈': ['variant_size', '옵션 사이즈'],
    'filter.v.availability': ['availability', '재고'],
    'filter.p.tag': ['tag', '태그'],
    'filter.p.m.custom.decades': ['decade', '연대'],
    'filter.p.m.custom.pants_fit': ['fit', '핏'],
    'filter.v.price.gte': ['price_min', '최저 가격'],
    'filter.v.price.lte': ['price_max', '최고 가격'],
  };
  function text(value, limit) {
    if (typeof value !== 'string' && typeof value !== 'number') return '';
    const result = String(value).trim();
    if (result.length > limit || /[<>\u0000-\u001f]/.test(result)
      || /[\w.+-]+@[\w.-]+\.[a-z]{2,}/i.test(result)
      || /(?:\+?82[- .]?)?0?1[016789][- .]?\d{3,4}[- .]?\d{4}/.test(result)) return '';
    return result;
  }
  function number(value) {
    if (typeof value !== 'number' && typeof value !== 'string') return undefined;
    if (typeof value === 'string' && !/^\d+(?:\.\d+)?$/.test(value)) return undefined;
    const result = Number(value);
    return Number.isFinite(result) && result >= 0 && result <= 1e12 ? result : undefined;
  }
  function resolveMoney(input) {
    const source = input && typeof input === 'object' ? input : {};
    const raw = source.raw_value;
    const currency = text(source.currency, 3).toUpperCase();
    const result = { raw_value: raw, currency: /^[A-Z]{3}$/.test(currency) ? currency : null,
      status: 'unresolved', rule: 'unknown_source_unit', unit_price: null };
    const amount = number(raw);
    if (amount === undefined || !result.currency) return Object.assign(result, { rule: 'invalid_money' });
    // Resolve only explicit unit+producer contracts. Missing source is never a divisor.
    if (source.source === 'shopify_liquid_hundredths' && source.unit === 'shopify_hundredths' && Number.isSafeInteger(amount)) {
      return Object.assign(result, { status: 'resolved', rule: 'divide_100_once', unit_price: amount / 100 });
    }
    if (source.source === 'shopify_customer_events' && source.unit === 'major') {
      return Object.assign(result, { status: 'resolved', rule: 'native_major_identity', unit_price: amount });
    }
    // Verified deployed legacy pixel schema1 reads Number(Money.amount) without
    // scaling. The source marker alone and conflicting explicit units are insufficient.
    if (source.source === 'shopify_customer_events' && source.schema_version === 1 && source.unit == null) {
      return Object.assign(result, { status: 'resolved', rule: 'native_schema1_major_identity', unit_price: amount });
    }
    if (source.source === 'theme_facade' && source.schema_version === 2 && source.unit === 'currency_major') {
      return Object.assign(result, { status: 'resolved', rule: 'theme_v2_major_identity', unit_price: amount });
    }
    if (source.source === 'canonical_v3' && source.schema_version === 3 && source.unit === 'major') {
      return Object.assign(result, { status: 'resolved', rule: 'canonical_major_identity', unit_price: amount });
    }
    return result;
  }
  function projectFilters(input, options) {
    const settings = options || {};
    const available = Array.isArray(input);
    const result = { filter_state_status: available ? 'empty' : 'unavailable', filter_tokens: [],
      filter_labels: [], filter_groups: [], active_brand_ids: [], active_brand_labels: [],
      active_recommended_sizes: [], active_tagged_sizes: [], active_variant_sizes: [],
      active_filter_count: 0, active_filters_truncated: settings.truncated === true,
      filter_state_label: '확인 불가', filter_tokens_key: '' };
    if (!available) return result;
    let invalid = false;
    const seen = new Set();
    for (const entry of input.slice(0, 20)) {
      if (!entry || typeof entry !== 'object' || !Object.hasOwn(groups, entry.param)) { invalid = true; continue; }
      const [group, title] = groups[entry.param];
      let id = text(entry.value, 180);
      let label = text(entry.label, 100);
      if (group === 'price_min' || group === 'price_max') {
        const amount = number(entry.value);
        if (amount === undefined) { invalid = true; continue; }
        id = String(amount);
        label = amount.toLocaleString('ko-KR');
        result[group === 'price_min' ? 'price_min' : 'price_max'] = amount;
      } else if (!id || (entry.param.startsWith('filter.p.m.custom.') && !/^gid:\/\/shopify\/Metaobject\/\d+$/.test(id))
        || (group === 'availability' && !['0', '1'].includes(id))
        || (group === 'tag' && !['new', 'store-only', 'coming-soon'].includes(id))) { invalid = true; continue; }
      const token = group + ':' + id;
      if (seen.has(token)) continue;
      seen.add(token);
      label = label || '이름 미확인 [' + id + ']';
      result.filter_tokens.push(token);
      result.filter_labels.push(title + ': ' + label);
      if (!result.filter_groups.includes(group)) result.filter_groups.push(group);
      if (group === 'brand') { result.active_brand_ids.push(id); result.active_brand_labels.push(label); }
      if (group === 'recommended_size') result.active_recommended_sizes.push(label);
      if (group === 'tagged_size') result.active_tagged_sizes.push(label);
      if (group === 'variant_size') result.active_variant_sizes.push(label);
    }
    result.active_filter_count = result.filter_tokens.length;
    result.active_filters_truncated = result.active_filters_truncated || input.length > 20;
    if (result.filter_tokens.join('|').length > 2048 || result.filter_labels.join(' · ').length > 2048) result.active_filters_truncated = true;
    result.filter_state_status = result.active_filters_truncated ? 'truncated' : invalid ? 'unavailable' : result.active_filter_count ? 'complete' : 'empty';
    result.filter_state_label = result.filter_labels.length ? result.filter_labels.join(' · ').slice(0, 2048) : result.filter_state_status === 'empty' ? '선택 없음' : '확인 불가';
    result.filter_tokens_key = result.filter_tokens.join('|').slice(0, 2048);
    return result;
  }
  return Object.freeze({ version: 1, resolveMoney, projectFilters });
});
